import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { DiaryEntry } from "./ClientDiary";

export function CompletionChecks({jobId, entries}: {jobId: string; entries: DiaryEntry[]}) {
  const [template, setTemplate] = useState("service");
  const [notice, setNotice] = useState("");
  const qc = useQueryClient();
  const refresh = () => { void qc.invalidateQueries({queryKey: ["diary"]}); };
  const apply = useMutation({mutationFn: () => api.diary.completionTemplate(jobId, template), onSuccess: result => {
    setNotice(result.added ? result.added+" checks added." : "This template is already on the job. Existing answers are unchanged."); refresh();
  }});
  const checks = entries.filter(e => e.kind === "completion_task");
  const open = checks.filter(e => e.status === "open").length;
  return <section style={{marginTop: 16}}>
    <h3>Completion checklist</h3>
    <p>Record checks and evidence before handing over the job. These are editable working prompts, not trade compliance certificates. Saving checks does not mark the job complete.</p>
    <div className="btn-row"><label>Starting template <select value={template} onChange={e => setTemplate(e.target.value)}>
      <option value="service">General service</option><option value="plumbing">Plumbing</option><option value="builder">Building / project stage</option><option value="security">Security installation</option>
    </select></label><button className="btn" disabled={apply.isPending} onClick={() => apply.mutate()}>Add completion checks</button></div>
    <p className="tiny muted">Add your own using Add diary entry → Completion check. Save selected checks as a reusable job pack for future jobs. Checks and notes require a connection.</p>
    {apply.error && <p role="alert">{apply.error.message}</p>}
    {notice && <p role="status">{notice}</p>}
    {!!checks.length && <p>{checks.length-open} of {checks.length} loaded checks addressed · {open} outstanding</p>}
    {checks.map(entry => <CompletionCheck key={entry.id+":"+entry.rev} entry={entry} onSaved={refresh} />)}
  </section>;
}

function CompletionCheck({entry, onSaved}: {entry: DiaryEntry; onSaved: () => void}) {
  const [status, setStatus] = useState(entry.status);
  const [note, setNote] = useState(entry.body);
  const save = useMutation({mutationFn: () => api.diary.completion(entry.id, {rev: entry.rev!, status, note}), onSuccess: onSaved});
  return <details style={{marginTop: 10}}>
    <summary>{entry.title} — {entry.status.replaceAll("_", " ")}</summary>
    <form className="stack" onSubmit={e => {e.preventDefault(); save.mutate();}}>
      <label className="field"><span>Result</span><select value={status} onChange={e => setStatus(e.target.value)}>
        <option value="open">Outstanding</option><option value="done">Done</option><option value="not_applicable">Not applicable</option>
      </select></label>
      <label className="field"><span>Notes / test results / evidence location</span><textarea rows={3} maxLength={20000} required={status === "not_applicable"} value={note} onChange={e => setNote(e.target.value)} /></label>
      {save.error && <p role="alert">{save.error.message}</p>}
      <button className="btn" disabled={save.isPending || (status === entry.status && note === entry.body)}>Save check</button>
    </form>
  </details>;
}
